import { mostRecentThread } from '@/features/clinic/ClinicScreen';
import { ClinicianAvatar } from '@/features/clinic/components/ClinicianAvatar';
import { useCareTeam, useThreads } from '@/lib/api/hooks';
import { cn } from '@/lib/cn';
import type { ThreadParticipant } from '@/types/api';

/** A provider as the drawer shows them (care-team entry or thread member). */
export type Provider = ThreadParticipant & {
  avatar_url?: string | null;
  role?: 'physician' | 'nurse';
};

/** The patient's providers, overlapped (e.g. Dr. Hinds + their nurse). */
export function ProviderAvatars({
  providers,
  size = 40,
}: {
  providers: Provider[];
  size?: number;
}) {
  const shown = providers.slice(0, 2);
  if (shown.length === 0) {
    return <ClinicianAvatar name="Care team" size={size} />;
  }
  return (
    <span className="flex shrink-0">
      {shown.map((p, i) => (
        <span
          key={p.id}
          className={cn('rounded-full', i > 0 && '-ml-12 ring-2 ring-[var(--surface-raised)]')}
        >
          <ClinicianAvatar name={p.display_name} src={p.avatar_url ?? undefined} size={size} />
        </span>
      ))}
    </span>
  );
}

/** System Support: the brand mark on a white round tile. */
export function SupportAvatar({ size = 40 }: { size?: number }) {
  return (
    <span
      className="shrink-0 rounded-full bg-page ring-1 ring-line-subtle flex items-center justify-center"
      style={{ width: size, height: size }}
    >
      <img
        src="/brand/system-s.png"
        alt=""
        className="object-contain"
        style={{ width: size * 0.55, height: size * 0.5 }}
      />
    </span>
  );
}

// The Healthie triage bot shares the patient's conversation and posts as
// "System Support" — it's not one of their providers.
const NOT_A_PROVIDER = /support/i;

export function providersFrom(participants: ThreadParticipant[] | undefined): Provider[] {
  return (participants ?? []).filter((p) => !NOT_A_PROVIDER.test(p.display_name));
}

/**
 * The patient's providers: their care team (physician + nurse) when the
 * backend knows it, else whoever is in the Healthie conversation.
 */
export function useProviders(): Provider[] {
  const { data: careTeam } = useCareTeam(true);
  const { data: threads } = useThreads();
  // Never present a support/bot account as a provider (e.g. a default
  // physician id pointed at the "System Support" Healthie user).
  const team = (careTeam?.providers ?? []).filter((p) => !NOT_A_PROVIDER.test(p.display_name));
  if (team.length) {
    return team.map((p) => ({
      id: p.id,
      display_name: p.display_name,
      title: p.title ?? '',
      avatar_url: p.avatar_url,
      role: p.role,
    }));
  }
  return providersFrom(mostRecentThread(threads ?? [])?.participants);
}

/**
 * "Your physician and nurse" from the care-team roles. Healthie titles are
 * credentials lists ("Registered Nurse (RN) · NLC · BLS · Licensed: …"), too
 * long for a card, so they're not used here.
 */
export function providerRoles(providers: Provider[]): string {
  const roles = new Set(providers.map((p) => p.role).filter(Boolean));
  if (roles.has('physician') && roles.has('nurse')) return 'Your physician and nurse';
  if (roles.has('physician')) return 'Your physician';
  if (roles.has('nurse')) return 'Your nurse';
  return providers.length > 1 ? 'Your providers' : 'Your provider';
}

/**
 * "Dr. Hinds & Jasmine W., RN" — the nurse carries her short credential so
 * it's clear who's who. A neutral label before any provider is known.
 */
export function providerNames(providers: Provider[]): string {
  const names = providers
    .slice(0, 2)
    .map((p) => (p.role === 'nurse' && p.title ? `${p.display_name}, ${p.title}` : p.display_name));
  return names.length ? names.join(' & ') : 'Your care team';
}
